import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Markdown } from '../lib/markdown';
import { useT } from '../i18n';
import dict from './Privacy.i18n';

/**
 * /privacy — renders the DRAFT Privacy Policy + DPA from public/legal/*.md, with a
 * clear "DRAFT — pending legal review" banner (DECISIONS.md). Linked from /login.
 * Public page (no auth).
 */
type Tab = 'privacy' | 'dpa';

export default function PrivacyPage() {
  const t = useT(dict);
  const [tab, setTab] = useState<Tab>('privacy');
  const [source, setSource] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const DOCS: Record<Tab, { label: string; path: string }> = {
    privacy: { label: t('tab_privacy'), path: '/legal/privacy-policy.md' },
    dpa: { label: t('tab_dpa'), path: '/legal/dpa.md' },
  };

  useEffect(() => {
    let active = true;
    setSource('');
    setError(null);
    fetch(DOCS[tab].path)
      .then((res) => {
        if (!res.ok)
          throw new Error(t('error_load_status', { status: res.status }));
        return res.text();
      })
      .then((text) => {
        if (active) setSource(text);
      })
      .catch((e: unknown) => {
        if (active) setError(e instanceof Error ? e.message : t('error_load'));
      });
    return () => {
      active = false;
    };
    // DOCS is recreated each render but only tab and t (lang) should re-trigger the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, t]);

  return (
    <div className="cl full col" style={{ height: '100vh', overflow: 'auto', background: 'var(--paper)' }}>
      <div
        className="row center between"
        style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
      >
        <div className="row center gap-8">
          <div
            style={{
              width: 24,
              height: 24,
              background: 'var(--red)',
              borderRadius: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              color: '#fff',
              fontSize: 14,
            }}
          >
            J
          </div>
          <div className="h2">{t('heading_legal')}</div>
        </div>
        <Link to="/login" className="btn sm">
          {t('back_to_sign_in')}
        </Link>
      </div>

      <div className="col gap-16" style={{ padding: '22px', maxWidth: 820, margin: '0 auto', width: '100%' }}>
        <div
          className="panel"
          style={{ padding: '12px 16px', borderColor: 'var(--amber)', background: 'var(--amber-soft)' }}
        >
          <strong>{t('draft_strong')}</strong>
          {t('draft_body')}
        </div>

        <div className="row gap-8">
          {(Object.keys(DOCS) as Tab[]).map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              className={`btn sm ${tabKey === tab ? 'primary' : ''}`}
              onClick={() => setTab(tabKey)}
            >
              {DOCS[tabKey].label}
            </button>
          ))}
        </div>

        <div className="panel" style={{ padding: '20px 24px' }}>
          {error ? (
            <div style={{ color: 'var(--red)' }}>{error}</div>
          ) : source ? (
            <Markdown source={source} />
          ) : (
            <div className="meta">{t('loading')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
